import { showToast, TOAST } from '@mimir/web/eventBus';
import type { UploadItem } from '@mimir/client-common/upload/UploadItem';
import { apiDeleteItem } from '@mimir/common/util/httpApi';
import { getGlobalStore } from '@mimir/web/store';
import type { FilesystemApis } from '@mimir/common/upload/fileOps';
import { createNodeFsApis } from '@mimir/common/nodejs/nodeFileOps';
import type { FileApi } from '@mimir/common/upload/fileApi';
import { isFileApi } from '@mimir/common/upload/fileApi';
import type { ManagedUploadItem } from '@mimir/common/upload/BasicUploadManager';
import { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import { UPLOAD_MULTIPART_THRESHOLD } from '@mimir/common/upload/limits';
import type { UploadManagerOptions} from '@mimir/common/upload/upload';
import { UploadState } from '@mimir/common/upload/upload';
import { invariant } from '@mimir/common/util/invariant';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { SearchResultHit } from '@mimir/common/dto/searchResultHit';
import { makePriority } from '@mimir/common/async/prioritySemaphore';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import { runTask } from '@mimir/common/async/taskScope';
import { ConsoleLogger } from '@mimir/common/util/logging';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';


export class UploadManager extends BasicUploadManager {

  constructor(protected applicationContext: ApplicationContext, options: UploadManagerOptions) {
    let fsApis: FilesystemApis | undefined = undefined;
    const nodeApi = applicationContext.nodeApi;
    if (nodeApi) {
      fsApis = createNodeFsApis(new ConsoleLogger(), nodeApi, true);
    }
    super(applicationContext, options, fsApis);
  }

  public isUploading(): boolean {
    /*
      Stubbed.

      Used by UploadTable to determine whether to offer the ability to pause and
      resume. Stubbed out at present, since the original code was not reliable
      was only partially implemented.

      TODO: Get rid of pause/resume completely, or implement it reliably using
      generic async pause points, passed in a similar fashion to the
      CancelToken.
    */
    return false;
  }

  public isPaused(): boolean {
    // Stubbed
    return false;
  }

  // Needed for uploading premiere project

  public async uploadDirect(uploadItem: UploadItem) {
    const priority = makePriority();
    const file = uploadItem.file;
    invariant(isFileApi(file), 'Upload item must have file');
    const fileItem = uploadItem as (typeof uploadItem & { file: FileApi });

    uploadItem.useMultiPart = file.size >= UPLOAD_MULTIPART_THRESHOLD;
    await uploadItem.beforeUpload(priority);
    await runTask({ priority, resilience: new ExponentialBackoff() }, async (tc) => this.uploadSingleItem(tc, fileItem));
  }

  public async cancelUpload(item: ManagedUploadItem) {
    await this.cancelUploadInternal(item);
  }

  public async pauseUpload() {
    // Stubbed.
  }

  public async resumeUpload(): Promise<void> {
    // Stubbed.
  }

  protected onConclusionChange(payload: { id: string, progressInfo: ProgressInfo}) {
    const update: { id: string, update: Partial<SearchResultHit>} = {
      id: payload.id,
      update: {
        progressInfo: payload.progressInfo
      }
    };
    const store = getGlobalStore();
    store.commit('updateSearchHitById', update);
    store.commit('updateCurrentItem', update);
    store.commit('updateCurrentPreviewItem', update);
  }

  protected reportBriefError(message: string) {
    showToast(message, TOAST.ERROR);
  }

  protected deleteItemUploadLock(itemId: string): Promise<void> {
    return getGlobalStore().dispatch('deleteItemUploadLock', itemId);
  }

  private async cancelUploadInternal(item: ManagedUploadItem) {
    if (item.id === undefined) {
      return;
    }

    item.markedForDeletion = true;
    const progressStatus = item.uploadProgress;

    item.uploadState = UploadState.CANCELLED;

    if (item.uploader) {
      item.uploader.cancel();
    }

    item.uploadProgress = progressStatus;
    if (item.isCreated && !item.isExistingItem) {
      await apiDeleteItem(globalBackendClient, item.id, true, false, undefined);
    }
  }

}
